/**
 * Cálculo canónico de pago de venta — usado por PuntoVenta, ventas.service y tests UI.
 * @module utils/ventaPagoCompute
 */

const CREDITO_METODOS = new Set([
  'credito',
  'cuenta corriente',
  'cuenta_corriente',
  'ctacte',
  'fiado'
]);

export function isMetodoCredito(metodoPago) {
  const m = String(metodoPago || '').trim().toLowerCase();
  return CREDITO_METODOS.has(m);
}

function toNumber(value) {
  if (value === null || value === undefined || value === '') return 0;
  const n = typeof value === 'number' ? value : parseFloat(String(value).trim().replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
}

/**
 * Normaliza monto pagado UI para crédito: anticipo parcial OK; pago completo stale → 0.
 * @param {number} total
 * @param {number} montoRaw
 * @returns {number}
 */
export function normalizeMontoCredito(total, montoRaw) {
  const totalNum = toNumber(total);
  let monto = Math.min(Math.max(0, toNumber(montoRaw)), totalNum);
  if (totalNum > 0 && monto >= totalNum) monto = 0;
  return monto;
}

/**
 * @param {{ metodoPago: string, total: number, montoPagado?: *, estadoPagoUi?: string }} input
 * @returns {{ monto_pagado: number, total_pagado: number, saldo_pendiente: number, estado: string, estado_pago: string }}
 */
export function computeVentaPago({ metodoPago, total, montoPagado, estadoPagoUi = 'pendiente' }) {
  const totalNum = toNumber(total);
  const esCredito = isMetodoCredito(metodoPago);

  if (esCredito) {
    const montoPagadoFinal = normalizeMontoCredito(totalNum, montoPagado);
    const saldoPendienteFinal = Math.max(0, totalNum - montoPagadoFinal);
    let estadoPagoFinal = 'pendiente';
    if (montoPagadoFinal > 0 && saldoPendienteFinal > 0) estadoPagoFinal = 'parcial';
    else if (saldoPendienteFinal <= 0 && montoPagadoFinal > 0) estadoPagoFinal = 'pagado';
    return {
      monto_pagado: montoPagadoFinal,
      total_pagado: montoPagadoFinal,
      saldo_pendiente: saldoPendienteFinal,
      estado: 'pendiente',
      estado_pago: estadoPagoFinal
    };
  }

  let montoPagadoFinal = toNumber(montoPagado);
  let estadoFinal = estadoPagoUi === 'completada' ? 'completada' : 'pendiente';
  let saldoPendienteFinal = 0;
  let estadoPagoFinal = 'pendiente';

  if (montoPagadoFinal >= totalNum && totalNum > 0) {
    estadoFinal = 'completada';
    montoPagadoFinal = totalNum;
    saldoPendienteFinal = 0;
    estadoPagoFinal = 'pagado';
  } else if (montoPagadoFinal > 0) {
    estadoFinal = 'pendiente';
    saldoPendienteFinal = totalNum - montoPagadoFinal;
    estadoPagoFinal = 'parcial';
  } else {
    estadoFinal = 'pendiente';
    montoPagadoFinal = 0;
    saldoPendienteFinal = totalNum;
    estadoPagoFinal = 'pendiente';
  }

  return {
    monto_pagado: montoPagadoFinal,
    total_pagado: montoPagadoFinal,
    saldo_pendiente: saldoPendienteFinal,
    estado: estadoFinal,
    estado_pago: estadoPagoFinal
  };
}

/**
 * Simula transición UI: Pago Completo → Crédito → payload a enviar.
 */
export function simulateUiCreditoTrasPagoCompleto(total) {
  const trasElegirCredito = {
    metodoPago: 'credito',
    montoPagado: null,
    estadoPagoUi: 'pendiente'
  };
  return computeVentaPago({ ...trasElegirCredito, total });
}
